import { getLeadCancellationReasonLabel } from '../constants/leadCancellationReason';
import { formatCurrency } from './formatting';
import {
  getBrazilCivilDate,
  getBrazilCurrentCompetenceMonth,
  getBrazilMonthBounds,
  getBrazilPreviousCompetenceMonth,
} from './competence';

export const COMMERCIAL_GOALS_PERIODS = [
  'today',
  'last7',
  'currentMonth',
  'previousMonth',
  'custom',
] as const;

export type CommercialGoalsPeriod = (typeof COMMERCIAL_GOALS_PERIODS)[number];

const MAX_RANGE_DAYS = 366;

export interface CommercialGoalsQuery {
  dateStart: string;
  dateEnd: string;
  goalMonth: string;
}

export type CommercialGoalsQueryResult =
  | { ok: true; query: CommercialGoalsQuery }
  | { ok: false; message: string };

export interface CommercialGoalsSeller {
  portalUserId: number | null;
  name: string | null;
  sales: number;
  revenueCents: number;
}

export interface CommercialGoalsLossReason {
  reason: string;
  count: number;
}

export interface CommercialGoalsDashboard {
  dateStart: string;
  dateEnd: string;
  goalMonth: string;
  goalStartsOn: string;
  goalEndsOn: string;
  leadsReceived: number;
  salesInPeriod: number;
  contractedRevenueCents: number;
  goalSalesCount: number;
  targetSales: number | null;
  sellers: CommercialGoalsSeller[];
  lossReasons: CommercialGoalsLossReason[];
}

export interface CommercialGoalsSellerRow {
  key: string;
  name: string;
  sales: number;
  revenueCents: number;
  barPercent: number;
}

export interface CommercialGoalsLossReasonRow {
  key: string;
  label: string;
  count: number;
  percent: number;
  barPercent: number;
}

export function resolveCommercialGoalsQuery(
  period: CommercialGoalsPeriod,
  customStart: string,
  customEnd: string,
  reference: Date = new Date(),
): CommercialGoalsQueryResult {
  if (period === 'custom') {
    return resolveCustomRange(customStart, customEnd);
  }

  const today = getBrazilCivilDate(reference);
  if (period === 'today') {
    return okQuery(today, today, getBrazilCurrentCompetenceMonth(reference));
  }

  if (period === 'last7') {
    return okQuery(
      getBrazilCivilDate(reference, -6),
      today,
      getBrazilCurrentCompetenceMonth(reference),
    );
  }

  if (period === 'previousMonth') {
    const goalMonth = getBrazilPreviousCompetenceMonth(reference);
    const bounds = getBrazilMonthBounds(goalMonth);
    if (!bounds) {
      return { ok: false, message: 'Não foi possível resolver o mês anterior.' };
    }
    return okQuery(bounds.start, bounds.end, goalMonth);
  }

  const goalMonth = getBrazilCurrentCompetenceMonth(reference);
  const bounds = getBrazilMonthBounds(goalMonth);
  if (!bounds) {
    return { ok: false, message: 'Não foi possível resolver o mês atual.' };
  }
  return okQuery(bounds.start, bounds.end, goalMonth);
}

export function commercialConversionRate(sales: number, leads: number): number | null {
  if (leads <= 0) {
    return null;
  }

  return (sales / leads) * 100;
}

export function commercialGoalProgress(sales: number, target: number | null): {
  remaining: number | null;
  percent: number | null;
  barPercent: number;
} {
  if (target == null || target <= 0) {
    return { remaining: target == null ? null : 0, percent: null, barPercent: 0 };
  }

  const percent = (sales / target) * 100;
  return {
    remaining: Math.max(0, target - sales),
    percent,
    barPercent: Math.min(100, percent),
  };
}

export function formatDashboardPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }

  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${text}%`;
}

export function formatDashboardCount(value: number): string {
  return value.toLocaleString('pt-BR');
}

export function formatContractedRevenue(cents: number): string {
  return formatCurrency(cents / 100);
}

export function formatCommercialGoalSummary(sales: number, target: number | null): string {
  if (target == null) {
    return `${formatDashboardCount(sales)} vendas | Sem meta`;
  }

  const remaining = Math.max(0, target - sales);
  return `${formatDashboardCount(sales)} de ${formatDashboardCount(target)} vendas | Faltam ${formatDashboardCount(remaining)}`;
}

export function formatCompetenceMonthLabel(month: string): string {
  const bounds = getBrazilMonthBounds(month);
  if (!bounds) {
    return month;
  }

  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${bounds.start}T00:00:00Z`));

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function isFullGoalMonth(
  dateStart: string,
  dateEnd: string,
  goalStartsOn: string,
  goalEndsOn: string,
): boolean {
  return dateStart === goalStartsOn && dateEnd === goalEndsOn;
}

export function buildSellerRows(sellers: CommercialGoalsSeller[]): CommercialGoalsSellerRow[] {
  const maxSales = sellers.reduce((max, seller) => Math.max(max, seller.sales), 0);

  return sellers.map((seller, index) => ({
    key: seller.portalUserId == null ? `unassigned-${index}` : String(seller.portalUserId),
    name: seller.name?.trim() || 'Sem vendedor',
    sales: seller.sales,
    revenueCents: seller.revenueCents,
    barPercent: barWidthPercent(seller.sales, maxSales),
  }));
}

export function buildLossReasonRows(
  reasons: CommercialGoalsLossReason[],
): CommercialGoalsLossReasonRow[] {
  const total = reasons.reduce((sum, reason) => sum + reason.count, 0);
  const maxCount = reasons.reduce((max, reason) => Math.max(max, reason.count), 0);

  return [...reasons]
    .filter((reason) => reason.count > 0)
    .map((reason) => ({
      key: reason.reason,
      label: reason.reason === 'other'
        ? 'Outros'
        : getLeadCancellationReasonLabel(reason.reason),
      count: reason.count,
      percent: total === 0 ? 0 : (reason.count / total) * 100,
      barPercent: barWidthPercent(reason.count, maxCount),
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'pt-BR'));
}

export function parseCommercialGoalsDashboard(value: unknown): CommercialGoalsDashboard | null {
  if (!isRecord(value)) {
    return null;
  }

  const dateStart = readIsoDate(value.dateStart);
  const dateEnd = readIsoDate(value.dateEnd);
  const goalMonth = readMonth(value.goalMonth);
  const goalStartsOn = readIsoDate(value.goalStartsOn);
  const goalEndsOn = readIsoDate(value.goalEndsOn);
  const leadsReceived = readNonNegativeInteger(value.leadsReceived);
  const salesInPeriod = readNonNegativeInteger(value.salesInPeriod);
  const contractedRevenueCents = readNonNegativeInteger(value.contractedRevenueCents);
  const goalSalesCount = readNonNegativeInteger(value.goalSalesCount);
  const targetSales = value.targetSales == null ? null : readNonNegativeInteger(value.targetSales);
  const sellers = readSellers(value.sellers);
  const lossReasons = readLossReasons(value.lossReasons);

  if (
    !dateStart
    || !dateEnd
    || !goalMonth
    || !goalStartsOn
    || !goalEndsOn
    || leadsReceived == null
    || salesInPeriod == null
    || contractedRevenueCents == null
    || goalSalesCount == null
    || (value.targetSales != null && targetSales == null)
    || !sellers
    || !lossReasons
  ) {
    return null;
  }

  return {
    dateStart,
    dateEnd,
    goalMonth,
    goalStartsOn,
    goalEndsOn,
    leadsReceived,
    salesInPeriod,
    contractedRevenueCents,
    goalSalesCount,
    targetSales,
    sellers,
    lossReasons,
  };
}

function okQuery(dateStart: string, dateEnd: string, goalMonth: string): CommercialGoalsQueryResult {
  return { ok: true, query: { dateStart, dateEnd, goalMonth } };
}

function resolveCustomRange(customStart: string, customEnd: string): CommercialGoalsQueryResult {
  if (!isIsoDate(customStart) || !isIsoDate(customEnd)) {
    return { ok: false, message: 'Informe a data inicial e a data final.' };
  }

  if (customStart > customEnd) {
    return { ok: false, message: 'A data inicial não pode ser maior que a data final.' };
  }

  const start = Date.parse(`${customStart}T00:00:00Z`);
  const end = Date.parse(`${customEnd}T00:00:00Z`);
  const days = Math.round((end - start) / 86_400_000);
  if (days > MAX_RANGE_DAYS) {
    return { ok: false, message: 'O período pode ter no máximo 366 dias.' };
  }

  return okQuery(customStart, customEnd, customEnd.slice(0, 7));
}

function barWidthPercent(value: number, max: number): number {
  if (max <= 0 || value <= 0) {
    return 0;
  }

  return Math.min(100, (value / max) * 100);
}

function isIsoDate(value: string): boolean {
  return readIsoDate(value) != null;
}

function readIsoDate(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return value;
}

function readMonth(value: unknown): string | null {
  return typeof value === 'string' && getBrazilMonthBounds(value) ? value : null;
}

function readNonNegativeInteger(value: unknown): number | null {
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : Number.NaN;

  if (!Number.isInteger(parsed) || parsed < 0 || parsed > Number.MAX_SAFE_INTEGER) {
    return null;
  }

  return parsed;
}

function readSellers(value: unknown): CommercialGoalsSeller[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const sellers: CommercialGoalsSeller[] = [];
  for (const item of value) {
    if (!isRecord(item)) {
      return null;
    }

    const sales = readNonNegativeInteger(item.sales);
    const revenueCents = readNonNegativeInteger(item.revenueCents);
    const portalUserId = item.portalUserId == null
      ? null
      : readNonNegativeInteger(item.portalUserId);
    const name = item.name == null ? null : typeof item.name === 'string' ? item.name : null;

    if (
      sales == null
      || revenueCents == null
      || (item.portalUserId != null && portalUserId == null)
      || (item.name != null && name == null)
    ) {
      return null;
    }

    sellers.push({ portalUserId, name, sales, revenueCents });
  }

  return sellers;
}

function readLossReasons(value: unknown): CommercialGoalsLossReason[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const reasons: CommercialGoalsLossReason[] = [];
  for (const item of value) {
    if (!isRecord(item) || typeof item.reason !== 'string' || item.reason.trim() === '') {
      return null;
    }

    const count = readNonNegativeInteger(item.count);
    if (count == null) {
      return null;
    }

    reasons.push({ reason: item.reason, count });
  }

  return reasons;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
